import type { PlacementGroup } from '@/modules/adv/ad-service'
import {
    INDEX_EXCHANGE,
    MEDIA_NET_MULTIFORMAT,
    MEDIA_NET_VIDEO,
    ONETAG,
    OMNIDEX_VIDEO,
    PUBMATIC_VIDEO,
    PLAYGAMA,
    PLAYGAMA_FALLBACK,
    TAPPX_VIDEO,
    LOOPME,
    GENIEE,
    ADITUDE_VIDEO,
    EQUATIV,
    APS,
    INMOBI_VIDEO,
} from '@/modules/adv/header-bidding/prebid/bidders-config'
import { type AdProvidersSettings, type GoogleAdSettings, type PlatformType, type EffectiveType } from '@/types'

import { getCommonProvidersAdSettings } from './common-providers-settings'
import { createHeaderBiddingAdSettings } from './provider-factories'
import { filterAdProviderSettings } from './utils'

const REWARDED_VIDEO_AUCTION_BIDDERS = [
    MEDIA_NET_VIDEO,
    PLAYGAMA,
    INMOBI_VIDEO,
    OMNIDEX_VIDEO,
    PUBMATIC_VIDEO,
    LOOPME,
    ADITUDE_VIDEO,
    EQUATIV,
    INDEX_EXCHANGE,
    TAPPX_VIDEO,
    ONETAG,
]

function withRewardedVideoAuction(settings: GoogleAdSettings): GoogleAdSettings {
    return {
        ...settings,
        options: {
            ...settings.options,
            rewardedVideoAuction: { bidders: REWARDED_VIDEO_AUCTION_BIDDERS, timeoutMs: 1000 },
        },
    }
}

export function getPortalAdSettings({
    gamAccount,
    platform,
    effectiveType,
    isH5RewardedAuction,
    isPlaygamaBidderOnly,
}: {
    gamAccount: string
    platform: PlatformType
    effectiveType: EffectiveType | undefined
    isH5RewardedAuction?: boolean
    isPlaygamaBidderOnly?: boolean
}): PlacementGroup<AdProvidersSettings>[] {
    const common = getCommonProvidersAdSettings(gamAccount, platform, isPlaygamaBidderOnly)

    const auctionEnabled = isH5RewardedAuction && platform === 'desktop'
    const fullscreenGroups: Array<{ placements: PlacementGroup<AdProvidersSettings>['placements']; auction: boolean }> =
        auctionEnabled
            ? [
                  { placements: ['interstitial', 'rewarded'], auction: true },
                  { placements: ['interstitial_preroll', 'interstitial_midroll'], auction: false },
              ]
            : [
                  {
                      placements: ['interstitial', 'interstitial_midroll', 'interstitial_preroll', 'rewarded'],
                      auction: false,
                  },
              ]

    return [
        {
            placements: ['*'],
            settings: [common.GOOGLE_AD_PORTAL_SETTINGS, common.HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS],
        },
        ...fullscreenGroups.map(({ placements, auction }) => ({
            placements,
            settings: [
                ...filterAdProviderSettings(effectiveType, [
                    auction
                        ? withRewardedVideoAuction(common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT)
                        : common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT,
                    common.GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
                    common.GOOGLE_AD_GAME_PAGE_SETTINGS_INTERSTITIAL_FORMAT,
                    common.GOOGLE_BANNER_GAME_PAGE_SETTINGS,
                ]),
                createHeaderBiddingAdSettings(platform, [
                    TAPPX_VIDEO,
                    MEDIA_NET_MULTIFORMAT,
                    ONETAG,
                    GENIEE,
                    ADITUDE_VIDEO,
                    OMNIDEX_VIDEO,
                    PUBMATIC_VIDEO,
                    LOOPME,
                    INMOBI_VIDEO,
                    EQUATIV,
                    INDEX_EXCHANGE,
                    APS,
                    PLAYGAMA_FALLBACK,
                ]),
            ],
        })),
    ]
}
